import { useEffect, useState, useCallback } from "react";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Sofa, Sparkles, Loader2, RefreshCw, AlertTriangle, Plus, Trash2, ImageIcon, Save } from "lucide-react";
import { toast } from "sonner";

function scrollToPhoto(photoId) {
  const el = document.querySelector(`[data-testid="detail-photo-${photoId}"]`);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  el.classList.add("ring-2", "ring-brand", "ring-offset-2");
  setTimeout(() => el.classList.remove("ring-2", "ring-brand", "ring-offset-2"), 2000);
}

export function Inventory({ propertyId, photosById = {}, rooms = [] }) {
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/properties/${propertyId}/inventory`);
      setInv(data || null);
    } catch (e) {
      console.error("load inventory failed", e);
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    setError(false);
    try {
      const { data } = await api.post(`/properties/${propertyId}/inventory`);
      setInv(data);
      setDirty(false);
    } catch (e) {
      console.error("generate inventory failed", e);
      setError(true);
    } finally {
      setGenerating(false);
    }
  };

  const save = async (patch = {}) => {
    setSaving(true);
    try {
      const body = { items: inv.items, include_in_pdf: inv.include_in_pdf, ...patch };
      const { data } = await api.patch(`/properties/${propertyId}/inventory`, body);
      setInv(data);
      setDirty(false);
      if (!("include_in_pdf" in patch)) toast.success("Inventory saved.");
    } catch (e) {
      console.error("save inventory failed", e);
      toast.error("Couldn't save — try again.");
    } finally {
      setSaving(false);
    }
  };

  const updateItem = (index, field, value) => {
    setInv((c) => ({ ...c, items: c.items.map((it, i) => i === index ? { ...it, [field]: value, user_confirmed: true } : it) }));
    setDirty(true);
  };

  const removeItem = (index) => {
    setInv((c) => ({ ...c, items: c.items.filter((_, i) => i !== index) }));
    setDirty(true);
  };

  const addRow = (room) => {
    setInv((c) => ({ ...c, items: [...c.items, { room, item_name: "", condition_note: "", source_photo_id: null, user_confirmed: true }] }));
    setDirty(true);
  };

  const togglePdf = (checked) => {
    setInv((c) => ({ ...c, include_in_pdf: checked }));
    save({ include_in_pdf: checked });
  };

  if (loading) return null;

  const Section = ({ children }) => (
    <div className="mt-6 rounded-2xl border border-[#E2E8F0] bg-white p-5 sm:p-6" data-testid="inventory-section">
      <div className="flex items-center gap-2">
        <Sofa size={20} className="text-brand" />
        <h2 className="font-heading text-xl font-semibold text-navy">Furniture &amp; appliance inventory</h2>
      </div>
      <p className="mt-1 text-sm text-[#64748B]">For furnished rentals, AI catalogues the landlord-provided items in your move-in photos. You are the final editor — review, edit, add, or remove any item.</p>
      {children}
    </div>
  );

  if (!inv) {
    return (
      <Section>
        {error ? (
          <div className="mt-4 rounded-xl border border-[#FECACA] bg-[#FEF2F2] p-4" data-testid="inventory-error">
            <p className="flex items-center gap-2 text-sm font-semibold text-[#B91C1C]"><AlertTriangle size={16} /> AI is temporarily unavailable.</p>
            <Button data-testid="inventory-retry-btn" onClick={generate} disabled={generating} className="mt-3 rounded-xl bg-brand hover:bg-brand-hover text-white">
              {generating ? <><Loader2 size={16} className="mr-2 animate-spin" /> Analyzing your move-in photos…</> : <><RefreshCw size={15} className="mr-2" /> Retry</>}
            </Button>
          </div>
        ) : (
          <Button data-testid="generate-inventory-btn" onClick={generate} disabled={generating} className="mt-4 rounded-xl bg-brand hover:bg-brand-hover text-white h-11 px-5">
            {generating ? <><Loader2 size={16} className="mr-2 animate-spin" /> Analyzing your move-in photos…</> : <><Sparkles size={16} className="mr-2" /> Generate inventory</>}
          </Button>
        )}
      </Section>
    );
  }

  const items = inv.items || [];
  const roomNames = rooms.length ? rooms.map((r) => r.name) : [];
  const groups = [];
  const idxByRoom = {};
  items.forEach((it, i) => {
    if (!(it.room in idxByRoom)) { idxByRoom[it.room] = groups.length; groups.push({ room: it.room, entries: [] }); }
    groups[idxByRoom[it.room]].entries.push({ ...it, index: i });
  });
  // rooms with no items yet (so user can still add)
  roomNames.forEach((rn) => { if (!(rn in idxByRoom)) { idxByRoom[rn] = groups.length; groups.push({ room: rn, entries: [] }); } });

  return (
    <Section>
      <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] px-4 py-3" data-testid="inventory-pdf-toggle-row">
        <div>
          <p className="text-sm font-semibold text-navy">Include in Evidence Package</p>
          <p className="text-xs text-[#64748B]">Adds an inventory section to your downloadable PDF.</p>
        </div>
        <Switch data-testid="inventory-pdf-toggle" checked={!!inv.include_in_pdf} onCheckedChange={togglePdf} />
      </div>

      {items.length === 0 && groups.every((g) => g.entries.length === 0) ? (
        <p className="mt-4 text-sm text-[#64748B]" data-testid="inventory-empty">No furniture or appliances were detected. This is expected for unfurnished rentals — you can still add items manually below.</p>
      ) : null}

      <div className="mt-5 space-y-5">
        {groups.map((g) => (
          <div key={g.room} data-testid={`inventory-room-${g.room}`}>
            <h3 className="font-heading text-base font-bold text-navy">{g.room}</h3>
            <div className="mt-2 space-y-2">
              {g.entries.map((it) => {
                const photo = it.source_photo_id ? photosById[it.source_photo_id] : null;
                return (
                  <div key={it.index} data-testid={`inventory-item-${it.index}`} className="flex items-start gap-2 rounded-xl border border-[#E2E8F0] px-3 py-2.5">
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2 min-w-0">
                      <Input data-testid={`inventory-item-name-${it.index}`} value={it.item_name} placeholder="Item (e.g. sofa)" onChange={(e) => updateItem(it.index, "item_name", e.target.value)} className="h-9 rounded-lg" />
                      <Input data-testid={`inventory-item-note-${it.index}`} value={it.condition_note} placeholder="Condition note" onChange={(e) => updateItem(it.index, "condition_note", e.target.value)} className="h-9 rounded-lg" />
                    </div>
                    {photo ? (
                      <button data-testid={`inventory-photo-${it.index}`} onClick={() => scrollToPhoto(it.source_photo_id)} title="View source move-in photo" className="h-9 w-9 rounded-lg overflow-hidden border border-[#E2E8F0] shrink-0 hover:ring-2 hover:ring-brand transition mt-0.5">
                        <img src={fileUrl(photo.storage_path)} alt="source" loading="lazy" className="w-full h-full object-cover" />
                      </button>
                    ) : <ImageIcon size={16} className="text-[#CBD5E1] shrink-0 mt-2.5" />}
                    <button data-testid={`inventory-remove-${it.index}`} onClick={() => removeItem(it.index)} title="Remove item" className="grid place-items-center h-9 w-9 rounded-lg text-[#94A3B8] hover:text-[#B91C1C] hover:bg-[#FEF2F2] shrink-0 mt-0.5">
                      <Trash2 size={16} />
                    </button>
                  </div>
                );
              })}
              <Button data-testid={`inventory-add-${g.room}`} variant="outline" size="sm" onClick={() => addRow(g.room)} className="rounded-lg border-dashed border-[#CBD5E1] text-[#475569]">
                <Plus size={14} className="mr-1.5" /> Add item
              </Button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <Button data-testid="inventory-save-btn" onClick={() => save()} disabled={saving || !dirty} className="rounded-xl bg-brand hover:bg-brand-hover text-white">
          {saving ? <><Loader2 size={15} className="mr-2 animate-spin" /> Saving…</> : <><Save size={15} className="mr-2" /> Save inventory</>}
        </Button>
        <Button data-testid="regenerate-inventory-btn" variant="outline" onClick={generate} disabled={generating} className="rounded-xl border-[#E2E8F0]">
          {generating ? <><Loader2 size={15} className="mr-2 animate-spin" /> Analyzing…</> : <><RefreshCw size={14} className="mr-2" /> Regenerate</>}
        </Button>
      </div>
    </Section>
  );
}
